import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { plain } from "@/lib/plain";
import { cn } from "@/lib/util";
import { Users, RefreshCw, Loader2, AtSign, Globe, Server, User, Radar, AlertTriangle } from "lucide-react";

const TYPE_ICON = { email: AtSign, subdomain: Globe, host: Globe, ip: Server, username: User };

const fmtDate = (v) => {
  if (!v) return "—";
  try {
    const d = new Date(v);
    return isNaN(d) ? String(v) : d.toLocaleString();
  } catch { return String(v); }
};

export function SocialIntelSection({ clients, clientId, onClientChange }) {
  const [data, setData] = useState({ records: [], stats: null, errors: [], last_scan_at: null, selectors: [] });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [typeFilter, setTypeFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const r = await api.get(`/social-intel/results/${clientId}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar Social Intel");
    } finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/social-intel/status").then(r => setStatus(r.data)).catch(() => {}); }, []);

  const runScan = async () => {
    if (!clientId) return;
    setScanning(true);
    try {
      const r = await api.post(`/social-intel/scan/${clientId}`);
      setData({ ...r.data, last_scan_at: r.data.last_scan_at });
      toast.success(`OSINT completo · ${r.data.records?.length || 0} registros`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo escanear");
    } finally { setScanning(false); }
  };

  const filtered = useMemo(() => {
    return (data.records || []).filter(r => {
      if (typeFilter !== "all" && r.type !== typeFilter) return false;
      if (sourceFilter !== "all" && !(r.source || "").startsWith(sourceFilter)) return false;
      return true;
    });
  }, [data.records, typeFilter, sourceFilter]);

  const typeCounts = useMemo(() => {
    const c = { email: 0, subdomain: 0, ip: 0, username: 0 };
    for (const r of data.records || []) c[r.type] = (c[r.type] || 0) + 1;
    return c;
  }, [data.records]);

  const sources = useMemo(() => {
    const s = new Set();
    for (const r of data.records || []) s.add((r.source || "").split(":")[0]);
    return Array.from(s);
  }, [data.records]);

  return (
    <section className="border border-border bg-card/60" data-testid="social-intel-section">
      <div className="p-5 border-b border-border flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-primary" />
          <h2 className="font-display text-lg font-medium">Social Media Intelligence</h2>
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground ml-2">
            theHarvester + crt.sh + hackertarget
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={clientId || ""} onChange={e => onClientChange?.(e.target.value)}
                  className="bg-background border border-border px-3 py-1.5 text-xs font-mono focus:border-primary outline-none"
                  data-testid="social-intel-client-selector">
            {clients.map(c => <option key={c.id} value={c.id}>{plain(c.name, `#${c.id.slice(0,8)}`)}</option>)}
          </select>
          <button onClick={load} disabled={!clientId || loading}
                  className="border border-border p-1.5 hover:border-primary hover:text-primary disabled:opacity-30"
                  data-testid="social-intel-reload-btn"
                  title="Recargar snapshot">
            <RefreshCw className={cn("w-3 h-3", loading && "animate-spin")} />
          </button>
          <button onClick={runScan} disabled={!clientId || scanning}
                  className="bg-primary text-primary-foreground px-3 py-1.5 font-mono text-[10px] tracking-widest uppercase inline-flex items-center gap-2 hover:bg-primary/90 disabled:opacity-50"
                  data-testid="social-intel-scan-btn">
            {scanning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Radar className="w-3 h-3" />}
            {scanning ? "Recolectando…" : "Recolectar OSINT"}
          </button>
        </div>
      </div>

      <div className="p-5 space-y-4">
        <div className="flex flex-wrap gap-4 text-[11px] font-mono text-muted-foreground">
          <div>
            <span className="uppercase tracking-widest">Último escaneo:</span>{" "}
            <span className="text-foreground" data-testid="social-intel-last-scan">
              {fmtDate(data.last_scan_at)}
            </span>
          </div>
          <div>
            <span className="uppercase tracking-widest">Selectores:</span>{" "}
            <span className="text-foreground">{data.selectors?.length || 0}</span>
          </div>
          <div>
            <span className="uppercase tracking-widest">theHarvester:</span>{" "}
            <span className={cn("uppercase tracking-widest",
              status?.theharvester_installed ? "text-sev-low" : "text-sev-high")}>
              {status?.theharvester_installed ? "instalado" : "no instalado (fallback HTTP)"}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatChip icon={AtSign} label="Emails" value={typeCounts.email} />
          <StatChip icon={Globe} label="Subdominios" value={typeCounts.subdomain} />
          <StatChip icon={Server} label="IPs" value={typeCounts.ip} />
          <StatChip icon={User} label="Perfiles" value={typeCounts.username} />
        </div>

        {data.errors?.length > 0 && (
          <details className="border border-sev-medium/40 bg-sev-medium/5 p-3 text-xs" data-testid="social-intel-errors">
            <summary className="cursor-pointer text-sev-medium font-mono uppercase tracking-widest text-[10px] inline-flex items-center gap-2">
              <AlertTriangle className="w-3 h-3" />
              {data.errors.length} advertencia{data.errors.length === 1 ? "" : "s"}
            </summary>
            <ul className="mt-2 space-y-1 font-mono text-[11px] text-muted-foreground">
              {data.errors.map((e, i) => <li key={i}>[{e.source}] {e.message}</li>)}
            </ul>
          </details>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Filtros:</span>
          {["all", "email", "subdomain", "ip", "username"].map(t => (
            <button key={t} onClick={() => setTypeFilter(t)}
                    className={cn("font-mono text-[10px] tracking-widest uppercase px-2 py-1 border",
                      typeFilter === t ? "border-primary text-primary bg-primary/10"
                                        : "border-border text-muted-foreground hover:text-foreground")}
                    data-testid={`social-intel-filter-${t}`}>
              {t}
            </button>
          ))}
          <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)}
                  className="bg-background border border-border px-2 py-1 text-[10px] font-mono focus:border-primary outline-none ml-auto"
                  data-testid="social-intel-source-filter">
            <option value="all">Todas las fuentes</option>
            {sources.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <div className="border border-border bg-background/40 max-h-[420px] overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="p-8 text-center text-xs font-mono text-muted-foreground">
              {data.records?.length === 0 ? "Sin datos. Ejecuta un escaneo para recolectar OSINT."
                                              : "Sin coincidencias con los filtros aplicados."}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-card border-b border-border">
                <tr className="text-left overline">
                  <th className="p-2">Tipo</th>
                  <th className="p-2">Valor</th>
                  <th className="p-2">Fuente</th>
                  <th className="p-2">Selector</th>
                  <th className="p-2">Encontrado</th>
                </tr>
              </thead>
              <tbody>
                {filtered.slice(0, 500).map((r, i) => {
                  const Icon = TYPE_ICON[r.type] || Globe;
                  return (
                    <tr key={i} className="border-b border-border/40 hover:bg-muted/20"
                        data-testid={`social-intel-row-${r.type}`}>
                      <td className="p-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                        <div className="inline-flex items-center gap-1"><Icon className="w-3 h-3" />{r.type}</div>
                      </td>
                      <td className="p-2 font-mono text-xs break-all">{r.value}</td>
                      <td className="p-2 font-mono text-[11px] text-muted-foreground">{r.source}</td>
                      <td className="p-2 font-mono text-[11px] text-muted-foreground">{r.selector}</td>
                      <td className="p-2 font-mono text-[10px] text-muted-foreground">{fmtDate(r.date_found)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        {filtered.length > 500 && (
          <div className="text-[10px] font-mono text-muted-foreground text-right">
            Mostrando 500 de {filtered.length} registros
          </div>
        )}
      </div>
    </section>
  );
}

function StatChip({ icon: Icon, label, value }) {
  return (
    <div className="border border-border p-3">
      <div className="overline flex items-center gap-1"><Icon className="w-3 h-3" />{label}</div>
      <div className="font-display text-2xl mt-1">{value || 0}</div>
    </div>
  );
}
